import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const formatText = (v) => (v != null && v !== '' ? String(v).trim() : '—');

const resolveEstatus = (data) => {
  if (!data) return { text: '—', pagado: false, hasStatus: false };
  if (data.estatus != null && String(data.estatus).trim() !== '') {
    const text = String(data.estatus).trim();
    return {
      text,
      pagado: text.toLowerCase() === 'pagado' || data.pagado === true,
      hasStatus: true,
    };
  }
  if (typeof data.pagado === 'boolean') {
    return {
      text: data.pagado ? 'Pagado' : 'Pendiente de pago',
      pagado: data.pagado,
      hasStatus: true,
    };
  }
  if (data.insmarcapago != null && data.insmarcapago !== '') {
    const pagado = Number(data.insmarcapago) === 1;
    return {
      text: pagado ? 'Pagado' : 'Pendiente de pago',
      pagado,
      hasStatus: true,
    };
  }
  return { text: '—', pagado: false, hasStatus: false };
};

const Credencial = () => {
  const { ref } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ref) {
      setError('Referencia no válida');
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const fetchCredencial = async () => {
      setLoading(true);
      setError(null);
      setData(null);

      const url = `${API_BASE_URL}/api/credencial/${encodeURIComponent(ref)}`;

      try {
        // cache: 'no-store' fuerza ir a la API/DB y no usar respuesta cacheada del navegador
        const res = await fetch(url, {
          method: 'GET',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });

        if (!res.ok) {
          if (res.status === 404) setError('Credencial no encontrada en la base de datos');
          else if (res.status >= 500) {
            setError(`Error del servidor al consultar la credencial (${API_BASE_URL})`);
          } else {
            setError(`Error al cargar la credencial (HTTP ${res.status})`);
          }
          return;
        }

        const json = await res.json();
        if (!json || (json.jugador == null && json.referencia == null && !json.eventual)) {
          setError('La API respondió sin datos de credencial');
          return;
        }
        setData(json);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('Credencial fetch failed:', url, err);
        setError(
          `No se pudo conectar con la API (${API_BASE_URL}). Verifica que el backend esté en línea y con acceso a la base de datos.`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCredencial();
    return () => controller.abort();
  }, [ref]);

  const isEventual = data?.eventual === true;
  const { text: estatusText, pagado: isPagado } = resolveEstatus(data);
  const credentialText = data && !isEventual
    ? [
        `Jugador: ${formatText(data.jugador)}`,
        `Club: ${formatText(data.club)}`,
        `Gira: ${formatText(data.gira)}`,
        `Válido hasta: ${formatText(data.validez)}`,
        'O hasta el inicio de la siguiente Gira Infantil Juvenil',
        `Estatus: ${estatusText}`,
      ].join('\n')
    : '';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, maxWidth: 400, mx: 'auto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 400, mx: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#002542' }}>
        Credencial Gira Infantil Juvenil
      </Typography>
      <Paper elevation={1} sx={{ p: 2, backgroundColor: '#fafafa' }}>
        {isEventual ? (
          <Box>
            <Typography variant="body1" color="text.secondary">
              Registro eventual
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                fontFamily: 'monospace',
                color: isPagado ? 'success.main' : 'warning.main',
                fontWeight: 'bold',
              }}
            >
              Estatus: {estatusText}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {credentialText}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default Credencial;
